import React, { useState, useEffect } from "react";
// import { toast } from 'react-toastify'; // Commented out - using react-toast now
import { toast } from "react-toast";
import axiosInstance from "../../api/axiosInstance";
import "../../styles/modal.css";

const MAX_NAME_CHARS = 16;

const truncateByChars = (text = "", maxChars = MAX_NAME_CHARS) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return { display: "", full: "", isTruncated: false };

  if (trimmed.length <= maxChars) {
    return { display: trimmed, full: trimmed, isTruncated: false };
  }

  return {
    display: `${trimmed.slice(0, maxChars)}...`,
    full: trimmed,
    isTruncated: true,
  };
};

const AISuggestionsModal = ({
  isOpen,
  onClose,
  testCase,
  onApplySuggestion,
}) => {
  const [suggestions, setSuggestions] = useState({
    refined_test_steps: [],
    refined_expected_results: [],
    refined_prerequisites: [],
  });
  const [loading, setLoading] = useState(true);
  const [appliedSuggestions, setAppliedSuggestions] = useState({
    improved_steps: [], // Array of {index, accepted, data}
    additional_expected_results: [], // Array of {index, accepted, data}
    missing_prerequisites: [], // Array of {index, accepted, data}
  });
  const [saveLoading, setSaveLoading] = useState(false);

  const normalizeSteps = (steps = []) => {
  return steps.map((step, index) => ({
    step_number: step.step_number || step.step || index + 1,
    action: step.action,
    description: step.description || step.expected || "",
  }));
};

  // Format test steps for display
  const formatTestStepsForDisplay = () => {
    if (!testCase || !testCase.steps) return "No steps provided.";

    // Check if steps is an object with step_description array
    if (
      testCase.steps.step_description &&
      Array.isArray(testCase.steps.step_description)
    ) {
      return testCase.steps.step_description
        .map((step, index) => `${index + 1}. ${step?.description || step || "no description"}`)
        .join("\n");
    }

    // If steps is a string, return it directly
    if (typeof testCase.steps === "string") {
      return testCase.steps;
    }

    return "No steps provided.";
  };

  // Generate AI suggestions when the modal opens
  useEffect(() => {
    if (isOpen && testCase) {
      setLoading(true);
      // Reset applied suggestions when modal opens
      setAppliedSuggestions({
        improved_steps: [],
        additional_expected_results: [],
        missing_prerequisites: [],
      });

      // Call the analyze API
      fetchAISuggestions();
    }
  }, [isOpen, testCase]);

  // Fetch AI suggestions from API
  const fetchAISuggestions = async () => {
    try {
      const response = await axiosInstance.post(
        `test_case/${testCase.id}/analyze`
      );

      console.log('this is the response ==',response);

      if (response && response.status_code == 200) {
        setSuggestions({
        refined_test_steps: normalizeSteps(response.data?.suggestions?.refined_test_steps),
        refined_expected_results: response.data?.suggestions?.refined_expected_results || [],
        refined_prerequisites: response.data?.suggestions?.refined_prerequisites || [],
      });
        // toast.success("AI suggestions generated successfully!");
      } else {
        toast.error("Failed to generate AI suggestions");
      }
    } catch (error) {
      console.error("Error fetching AI suggestions:", error);
      if (error.response) {
        toast.error(
          `Failed to generate suggestions: ${
            error.response?.message || error.response.statusText
          }`
        );
      } else if (error.request) {
        toast.error("Failed to generate suggestions: No response from server");
      } else {
        toast.error(`Failed to generate suggestions: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle accepting/rejecting individual items by category and index
  const handleToggleItem = (category, index, action) => {
    const stepData = category === 'improved_steps' ? suggestions.refined_test_steps[index] :
                     category === 'additional_expected_results' ? suggestions.refined_expected_results[index] :
                     suggestions.refined_prerequisites[index];

    setAppliedSuggestions((prev) => {
      const currentItems = prev[category] || [];
      const existingItemIndex = currentItems.findIndex(item => item.index === index);
      
      if (action === 'accept') {
        if (existingItemIndex >= 0) {
          // Update existing item
          const updatedItems = [...currentItems];
          updatedItems[existingItemIndex] = { index, accepted: true, data: stepData };
          return { ...prev, [category]: updatedItems };
        } else {
          // Add new accepted item
          return { 
            ...prev, 
            [category]: [...currentItems, { index, accepted: true, data: stepData }]
          };
        }
      } else { // reject
        if (existingItemIndex >= 0) {
          // Update existing item to rejected
          const updatedItems = [...currentItems];
          updatedItems[existingItemIndex] = { index, accepted: false, data: stepData };
          return { ...prev, [category]: updatedItems };
        } else {
          // Add new rejected item
          return { 
            ...prev, 
            [category]: [...currentItems, { index, accepted: false, data: stepData }]
          };
        }
      }
    });

    // Show appropriate toast message
    const sectionName = category === 'improved_steps' ? 'Test Step' :
                       category === 'additional_expected_results' ? 'Expected Result' :
                       'Prerequisite';
    
    if (action === 'accept') {
      toast.success(`${sectionName} ${index + 1} accepted!`);
    } else {
      toast.success(`${sectionName} ${index + 1} rejected!`);
    }
  };

  // Helper function to get item status
  const getItemStatus = (category, index) => {
    const items = appliedSuggestions[category] || [];
    const item = items.find(item => item.index === index);
    return item ? item.accepted : null; // null means not decided, true = accepted, false = rejected
  };

  // Handle saving changes
  const handleSaveChanges = async () => {
    // Check if any suggestions are accepted
    const acceptedImprovedSteps = appliedSuggestions.improved_steps.filter(item => item.accepted === true);
    const acceptedExpectedResults = appliedSuggestions.additional_expected_results.filter(item => item.accepted === true);
    const acceptedPrerequisites = appliedSuggestions.missing_prerequisites.filter(item => item.accepted === true);

    const hasAcceptedChanges = acceptedImprovedSteps.length > 0 || acceptedExpectedResults.length > 0 || acceptedPrerequisites.length > 0;

    if (!hasAcceptedChanges) {
      toast.warning("Please accept at least one suggestion before saving.");
      return;
    }

    setSaveLoading(true);
    try {
      const payload = {
        test_case_id: testCase.id,
        improved_steps: acceptedImprovedSteps.map(item => item.data),
        additional_expected_results: acceptedExpectedResults.map(item => item.data),
        missing_prerequisites: acceptedPrerequisites.map(item => item.data),
      };

      const response = await axiosInstance.post(
        `test_case/${testCase.id}/apply-suggestions`,
        payload
      );

      if (response && response.status_code === 200) {
        toast.success("Suggestions applied successfully!");
        if (onApplySuggestion) {
          onApplySuggestion(response);
        }
        onClose();
      } else {
        toast.error("Failed to apply suggestions");
      }
    } catch (error) {
      console.error("Error applying suggestions:", error);
      if (error.response) {
        toast.error(
          `Failed to apply suggestions: ${
            error.response.data?.message || error.response.statusText
          }`
        );
      } else if (error.request) {
        toast.error("Failed to apply suggestions: No response from server");
      } else {
        toast.error(`Failed to apply suggestions: ${error.message}`);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  if (!isOpen || !testCase) return null;
  const truncatedTestCaseName = truncateByChars(testCase?.name);

  return (
    <>
      <div data-rt-modal-overlay className="modal-overlay" onClick={onClose} style={{zIndex:14000}} ></div>
      <div
        className="modal ai-suggestions-modal"
        style={{
          maxWidth: "800px",
          width: "90%",
          maxHeight: "90vh",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          zIndex:14500
        }}
      >
        <div
          className="modal-header"
          style={{
            borderBottom: "1px solid #e9ecef",
            padding: "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2
            style={{
              margin: "0",
              fontSize: "1.4rem",
              fontWeight: "600",
              color: "#212529",
            }}
          >
            AI Suggestions for Test Case Improvement
          </h2>
          <button className="modal-close" onClick={onClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div
          className="modal-body"
          style={{ padding: "24px", maxHeight: "60vh", overflowY: "auto" }}
        >
          {loading ? (
            <div
              className="loading-container"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "40px 0",
              }}
            >
              <div
                className="loading-spinner"
                style={{
                  width: "40px",
                  height: "40px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #3498db",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
              <p style={{ marginTop: "16px", color: "#666" }}>
                Generating AI suggestions...
              </p>
            </div>
          ) : (
            <>
              <div
                className="test-case-details"
                style={{
                  backgroundColor: "#f8f9fa",
                  padding: "16px",
                  borderRadius: "8px",
                  marginBottom: "24px",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "1rem",
                    fontWeight: "600",
                    color: "#212529",
                  }}
                >
                  Current Test Case
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div>
                    <p style={{ margin: "0 0 8px 0", fontWeight: "500" }}>
                      Name:
                    </p>
                    <p style={{ margin: "0 0 16px 0", color: "#495057" }}>
                      <span title={truncatedTestCaseName.isTruncated ? truncatedTestCaseName.full : undefined}>
                        {truncatedTestCaseName.display}
                      </span>
                    </p>

                    <p style={{ margin: "0 0 8px 0", fontWeight: "500" }}>
                      Description:
                    </p>
                    <p style={{ margin: "0 0 16px 0", color: "#495057" }}>
                      {testCase.description || "No description provided."}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: "0 0 8px 0", fontWeight: "500" }}>
                      Steps:
                    </p>
                    <pre
                      style={{
                        margin: "0 0 16px 0",
                        color: "#495057",
                        whiteSpace: "pre-wrap",
                        fontFamily: "inherit",
                        fontSize: "0.9rem",
                        lineHeight: "1.4",
                      }}
                    >
                      {formatTestStepsForDisplay()}
                    </pre>

                    <p style={{ margin: "0 0 8px 0", fontWeight: "500" }}>
                      Expected Result:
                    </p>
                    <p style={{ margin: "0", color: "#495057" }}>
                      {testCase.expectedResult ||
                        testCase.expected_result ||
                        "No expected result provided."}
                    </p>
                  </div>
                </div>
              </div>

              <div className="ai-suggestions">
                <h3
                  style={{
                    margin: "0 0 16px 0",
                    fontSize: "1.1rem",
                    fontWeight: "600",
                    color: "#212529",
                  }}
                >
                  AI Suggestions
                </h3>

                {/* Debug logging */}

                {/* Refine Test Steps Section */}
                {suggestions.refined_test_steps &&
                  suggestions.refined_test_steps.length > 0 && (
                    <div
                      className="suggestion-category"
                      style={{ marginBottom: "24px" }}
                    >
                      <h4
                        style={{
                          margin: "0 0 12px 0",
                          fontSize: "1rem",
                          fontWeight: "600",
                          color: "#28a745",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                        Refine Test Steps
                      </h4>

                      <div
                        style={{
                          backgroundColor: "#f0fff4",
                          padding: "16px",
                          borderRadius: "8px",
                          marginBottom: "12px",
                        }}
                      >
                        <div>
                          {suggestions.refined_test_steps.map((step, index) => (
                            <div key={index} style={{ 
                              marginBottom: "16px", 
                              padding: "12px",
                              border: "1px solid #e0e0e0",
                              borderRadius: "6px",
                              backgroundColor: "#fff"
                            }}>
                              <div style={{ 
                                fontSize: "0.9rem", 
                                color: "#495057", 
                                marginBottom: "8px",
                                lineHeight: "1.4"
                              }}>
                             <strong>Step {step.step || step.step_number || index + 1}:</strong>
  
                              <div>
                                <b>Action:</b> {step.action || "N/A"}
                              </div>

                              <div>
                                <b>Description:</b> {step.description || step.expected || "N/A"}
                              </div>
                              </div>
                              
                              <div style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "8px",
                              }}>
                                <button
                                  className="reject-suggestion-button"
                                  style={{
                                    backgroundColor: getItemStatus('improved_steps', index) === false ? "#dc3545" : "#6c757d",
                                    color: "white",
                                    border: "none",
                                    padding: "6px 12px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "0.8rem",
                                    fontWeight: "500",
                                  }}
                                  onClick={() => handleToggleItem('improved_steps', index, 'reject')}
                                >
                                  {getItemStatus('improved_steps', index) === false ? "Rejected ✗" : "Reject"}
                                </button>
                                <button
                                  className="accept-suggestion-button"
                                  style={{
                                    backgroundColor: getItemStatus('improved_steps', index) === true ? "#28a745" : "#6c757d",
                                    color: "white",
                                    border: "none",
                                    padding: "6px 12px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "0.8rem",
                                    fontWeight: "500",
                                  }}
                                  onClick={() => handleToggleItem('improved_steps', index, 'accept')}
                                >
                                  {getItemStatus('improved_steps', index) === true ? "Accepted ✓" : "Accept"}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Additional Expected Results Section */}
                {suggestions.refined_expected_results &&
                  suggestions.refined_expected_results.length > 0 && (
                    <div
                      className="suggestion-category"
                      style={{ marginBottom: "24px" }}
                    >
                      <h4
                        style={{
                          margin: "0 0 12px 0",
                          fontSize: "1rem",
                          fontWeight: "600",
                          color: "#0062cc",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                        Additional Expected Results
                      </h4>

                      <div
                        style={{
                          backgroundColor: "#f0f7ff",
                          padding: "16px",
                          borderRadius: "8px",
                          marginBottom: "12px",
                        }}
                      >
                        <div>
                          {suggestions.refined_expected_results.map((result, index) => (
                            <div key={index} style={{ 
                              marginBottom: "16px", 
                              padding: "12px",
                              border: "1px solid #e0e0e0",
                              borderRadius: "6px",
                              backgroundColor: "#fff"
                            }}>
                              <div style={{ 
                                fontSize: "0.9rem", 
                                color: "#495057", 
                                marginBottom: "8px",
                                lineHeight: "1.4"
                              }}>
                                <strong>Expected Result {index + 1}:</strong> {result}
                              </div>
                              
                              <div style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "8px",
                              }}>
                                <button
                                  className="reject-suggestion-button"
                                  style={{
                                    backgroundColor: getItemStatus('additional_expected_results', index) === false ? "#dc3545" : "#6c757d",
                                    color: "white",
                                    border: "none",
                                    padding: "6px 12px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "0.8rem",
                                    fontWeight: "500",
                                  }}
                                  onClick={() => handleToggleItem('additional_expected_results', index, 'reject')}
                                >
                                  {getItemStatus('additional_expected_results', index) === false ? "Rejected ✗" : "Reject"}
                                </button>
                                <button
                                  className="accept-suggestion-button"
                                  style={{
                                    backgroundColor: getItemStatus('additional_expected_results', index) === true ? "#0062cc" : "#6c757d",
                                    color: "white",
                                    border: "none",
                                    padding: "6px 12px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "0.8rem",
                                    fontWeight: "500",
                                  }}
                                  onClick={() => handleToggleItem('additional_expected_results', index, 'accept')}
                                >
                                  {getItemStatus('additional_expected_results', index) === true ? "Accepted ✓" : "Accept"}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Missing Prerequisites Section */}
                {suggestions.refined_prerequisites &&
                  suggestions.refined_prerequisites.length > 0 && (
                    <div className="suggestion-category">
                      <h4
                        style={{
                          margin: "0 0 12px 0",
                          fontSize: "1rem",
                          fontWeight: "600",
                          color: "#dc3545",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="8" x2="12" y2="12"></line>
                          <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        Missing Prerequisites
                      </h4>

                      <div
                        style={{
                          backgroundColor: "#fff5f5",
                          padding: "16px",
                          borderRadius: "8px",
                          marginBottom: "12px",
                        }}
                      >
                        <div>
                          {suggestions.refined_prerequisites.map((prerequisite, index) => (
                            <div key={index} style={{ 
                              marginBottom: "16px", 
                              padding: "12px",
                              border: "1px solid #e0e0e0",
                              borderRadius: "6px",
                              backgroundColor: "#fff"
                            }}>
                              <div style={{ 
                                fontSize: "0.9rem", 
                                color: "#495057", 
                                marginBottom: "8px",
                                lineHeight: "1.4"
                              }}>
                                <strong>Prerequisite {index + 1}:</strong> {prerequisite}
                              </div>
                              
                              <div style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "8px",
                              }}>
                                <button
                                  className="reject-suggestion-button"
                                  style={{
                                    backgroundColor: getItemStatus('missing_prerequisites', index) === false ? "#dc3545" : "#6c757d",
                                    color: "white",
                                    border: "none",
                                    padding: "6px 12px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "0.8rem",
                                    fontWeight: "500",
                                  }}
                                  onClick={() => handleToggleItem('missing_prerequisites', index, 'reject')}
                                >
                                  {getItemStatus('missing_prerequisites', index) === false ? "Rejected ✗" : "Reject"}
                                </button>
                                <button
                                  className="accept-suggestion-button"
                                  style={{
                                    backgroundColor: getItemStatus('missing_prerequisites', index) === true ? "#dc3545" : "#6c757d",
                                    color: "white",
                                    border: "none",
                                    padding: "6px 12px",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "0.8rem",
                                    fontWeight: "500",
                                  }}
                                  onClick={() => handleToggleItem('missing_prerequisites', index, 'accept')}
                                >
                                  {getItemStatus('missing_prerequisites', index) === true ? "Accepted ✓" : "Accept"}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                {/* No suggestions message */}
                {(!suggestions.refined_test_steps ||
                  suggestions.refined_test_steps.length === 0) &&
                  (!suggestions.refined_expected_results ||
                    suggestions.refined_expected_results.length === 0) &&
                  (!suggestions.refined_prerequisites ||
                    suggestions.refined_prerequisites.length === 0) && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px 0",
                        color: "#666",
                      }}
                    >
                      <p>No AI suggestions available for this test case.</p>
                    </div>
                  )}
              </div>
            </>
          )}
        </div>

        <div
          className="modal-footer"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            borderTop: "1px solid #e9ecef",
            backgroundColor: "#f8f9fa",
          }}
        >
          <div style={{ fontSize: "0.9rem", color: "#666" }}>
            {(() => {
              const acceptedImprovedSteps = appliedSuggestions.improved_steps.filter(item => item.accepted === true).length;
              const acceptedExpectedResults = appliedSuggestions.additional_expected_results.filter(item => item.accepted === true).length;
              const acceptedPrerequisites = appliedSuggestions.missing_prerequisites.filter(item => item.accepted === true).length;
              
              const statusParts = [];
              if (acceptedImprovedSteps > 0) statusParts.push(`${acceptedImprovedSteps} Test Steps`);
              if (acceptedExpectedResults > 0) statusParts.push(`${acceptedExpectedResults} Expected Results`);
              if (acceptedPrerequisites > 0) statusParts.push(`${acceptedPrerequisites} Prerequisites`);
              
              return statusParts.length > 0 ? (
                <span>{statusParts.join(", ")} accepted</span>
              ) : null;
            })()}
          </div>

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              className="btn btn-outline"
              style={{
                padding: "8px 16px",
                backgroundColor: "white",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={onClose}
              disabled={saveLoading}
            >
              Close
            </button>

            <button
              className="btn btn-primary"
              style={{
                padding: "8px 20px",
                backgroundColor: (() => {
                  const hasAccepted = appliedSuggestions.improved_steps.some(item => item.accepted === true) ||
                                    appliedSuggestions.additional_expected_results.some(item => item.accepted === true) ||
                                    appliedSuggestions.missing_prerequisites.some(item => item.accepted === true);
                  return hasAccepted ? "#007bff" : "#6c757d";
                })(),
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: (() => {
                  const hasAccepted = appliedSuggestions.improved_steps.some(item => item.accepted === true) ||
                                    appliedSuggestions.additional_expected_results.some(item => item.accepted === true) ||
                                    appliedSuggestions.missing_prerequisites.some(item => item.accepted === true);
                  return hasAccepted ? "pointer" : "not-allowed";
                })(),
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onClick={handleSaveChanges}
              disabled={(() => {
                const hasAccepted = appliedSuggestions.improved_steps.some(item => item.accepted === true) ||
                                  appliedSuggestions.additional_expected_results.some(item => item.accepted === true) ||
                                  appliedSuggestions.missing_prerequisites.some(item => item.accepted === true);
                return saveLoading || !hasAccepted;
              })()}
            >
              {saveLoading && (
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid transparent",
                    borderTop: "2px solid white",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                ></div>
              )}
              {saveLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AISuggestionsModal;
